import React, { useEffect, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import "./NutritionAdmin.css";

// Nutrición del entrenador, como en Harbiz: librería de recetas y planes
// (plantilla diaria con opciones por comida) que se asignan a los clientes.

const TIPOS = ["Desayuno", "Almuerzo", "Merienda", "Cena", "Snack"];
const MACROS = [
  ["calorias", "Kcal"],
  ["proteinas", "Proteínas (g)"],
  ["carbohidratos", "Carbohidratos (g)"],
  ["grasas", "Grasas (g)"],
];
const MAX_LISTA = 60;

const conToken = async () => ({
  headers: { Authorization: `Bearer ${(await getSession()).token}` },
});
const mensajeError = (err, porDefecto) => err.response?.data?.error || porDefecto;
// "" -> undefined, para que el backend lo trate como "sin dato"
const numero = (v) => (v === "" || v == null ? undefined : Number(v));
const total = (opcion, k) => Math.round((Number(opcion[k]) || 0) * (Number(opcion.raciones) || 0));

export default function NutritionAdmin() {
  const [vista, setVista] = useState("planes");
  const [recetas, setRecetas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const cargarRecetas = () =>
    axios.get(`${API_URL}/recipes`).then((res) => setRecetas(res.data)).catch(() => {});

  useEffect(() => {
    cargarRecetas();
    conToken()
      .then((cfg) => axios.get(`${API_URL}/admin/users`, cfg))
      .then((res) => setUsuarios(res.data.filter((u) => u.role !== "admin")))
      .catch(() => {});
  }, []);

  return (
    <div className="nutri">
      <div className="nutri-vistas" role="tablist" aria-label="Sección">
        {[["planes", "Planes"], ["recetas", "Recetas"]].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={vista === key}
            className={"nutri-vista" + (vista === key ? " is-active" : "")}
            onClick={() => setVista(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {vista === "planes" ? (
        <Planes recetas={recetas} usuarios={usuarios} />
      ) : (
        <Recetas recetas={recetas} recargar={cargarRecetas} />
      )}
    </div>
  );
}

/* ---------- Planes ---------- */

const planVacio = () => ({
  nombre: "",
  indicaciones: "",
  objetivos: {},
  comidas: ["Desayuno", "Almuerzo", "Merienda", "Cena"].map((tipo) => ({ tipo, opciones: [] })),
  asignados: [],
});

function Planes({ recetas, usuarios }) {
  const [planes, setPlanes] = useState(null);
  const [editando, setEditando] = useState(null);
  const [borrar, setBorrar] = useState(null);
  const [error, setError] = useState("");

  const cargar = async () => {
    try {
      const res = await axios.get(`${API_URL}/nutrition-plans`, await conToken());
      setPlanes(res.data);
    } catch (err) {
      setError(mensajeError(err, "No se pudieron cargar los planes."));
    }
  };
  useEffect(() => {
    cargar();
  }, []);

  const borrarPlan = async (id) => {
    try {
      await axios.delete(`${API_URL}/nutrition-plans/${id}`, await conToken());
      setBorrar(null);
      cargar();
    } catch (err) {
      setError(mensajeError(err, "No se pudo borrar el plan."));
    }
  };

  if (editando) {
    return (
      <PlanEditor
        inicial={editando}
        recetas={recetas}
        usuarios={usuarios}
        onCancelar={() => setEditando(null)}
        onGuardado={() => {
          setEditando(null);
          cargar();
        }}
      />
    );
  }

  return (
    <>
      <div className="nutri-barra">
        <p className="app-sub nutri-sub">
          Plantillas de un día con varias opciones por comida. Asígnalas a tus clientes y las verán en su "Plan de comidas".
        </p>
        <button className="app-btn" onClick={() => setEditando(planVacio())}>
          + Nuevo plan
        </button>
      </div>
      {error && <div className="app-estado app-estado-error">{error}</div>}
      {!planes && !error && <div className="app-estado">Cargando planes…</div>}
      {planes?.length === 0 && (
        <div className="app-estado">Aún no hay planes. Crea el primero y asígnalo a tus clientes.</div>
      )}
      <div className="nutri-lista">
        {planes?.map((p) => (
          <article key={p._id} className="app-card nutri-plan">
            <div>
              <h3>{p.nombre}</h3>
              <p className="nutri-meta">
                {p.comidas.length} comidas ·{" "}
                {p.comidas.reduce((s, c) => s + total(c.opciones[0] || {}, "calorias"), 0)} kcal con la opción 1
                {p.objetivos?.calorias ? ` · objetivo ${p.objetivos.calorias} kcal` : " · objetivo automático"}
              </p>
              <p className="nutri-meta">
                {p.asignados.length
                  ? `Asignado a ${p.asignados.map((u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email).join(", ")}`
                  : "Sin asignar"}
              </p>
            </div>
            <div className="nutri-acciones">
              <button
                className="app-btn app-btn-quiet app-btn-sm"
                onClick={() =>
                  setEditando({
                    ...p,
                    // Las opciones sin receta son alimentos libres
                    comidas: p.comidas.map((c) => ({
                      ...c,
                      opciones: c.opciones.map((o) => ({ ...o, libre: !o.recetaId })),
                    })),
                    asignados: p.asignados.map((u) => u._id),
                  })
                }
              >
                Editar
              </button>
              {borrar === p._id ? (
                <button className="app-btn app-btn-danger app-btn-sm" onClick={() => borrarPlan(p._id)}>
                  Confirmar borrado
                </button>
              ) : (
                <button className="app-btn app-btn-quiet app-btn-sm" onClick={() => setBorrar(p._id)}>
                  Borrar
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function PlanEditor({ inicial, recetas, usuarios, onCancelar, onGuardado }) {
  const [plan, setPlan] = useState(inicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const cambiar = (campo, valor) => setPlan((p) => ({ ...p, [campo]: valor }));
  const cambiarComida = (i, cambio) =>
    setPlan((p) => ({ ...p, comidas: p.comidas.map((c, j) => (j === i ? { ...c, ...cambio } : c)) }));
  const cambiarOpcion = (i, k, cambio) =>
    cambiarComida(i, {
      opciones: plan.comidas[i].opciones.map((o, j) => (j === k ? { ...o, ...cambio } : o)),
    });

  // Copia de la receta elegida (valores de una ración)
  const elegirReceta = (i, k, nombre) => {
    const r = recetas.find((x) => x.nombre === nombre);
    cambiarOpcion(
      i,
      k,
      r
        ? {
            recetaId: r._id,
            nombre: r.nombre,
            calorias: r.calorias,
            proteinas: r.proteinas,
            carbohidratos: r.carbohidratos,
            grasas: r.grasas,
            tiempo: r.tiempo,
            ingredientes: r.ingredientes,
            preparacion: r.descripcion,
            fuente: r.fuente,
          }
        : { recetaId: undefined, nombre }
    );
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    const vacia = plan.comidas.find((c) => c.opciones.length === 0);
    if (vacia) return setError(`"${vacia.tipo || "Una comida"}" no tiene ninguna opción.`);
    const sinReceta = plan.comidas.some((c) => c.opciones.some((o) => !o.libre && !o.recetaId));
    if (sinReceta) return setError("Hay una opción sin receta: elígela de la lista o bórrala.");

    const cuerpo = {
      ...plan,
      objetivos: Object.fromEntries(MACROS.map(([k]) => [k, numero(plan.objetivos?.[k])])),
      comidas: plan.comidas.map((c) => ({
        tipo: c.tipo,
        opciones: c.opciones.map(({ libre, ...o }) => ({
          ...o,
          raciones: numero(o.raciones),
          calorias: numero(o.calorias),
          proteinas: numero(o.proteinas),
          carbohidratos: numero(o.carbohidratos),
          grasas: numero(o.grasas),
        })),
      })),
    };
    setGuardando(true);
    try {
      const cfg = await conToken();
      if (plan._id) await axios.put(`${API_URL}/nutrition-plans/${plan._id}`, cuerpo, cfg);
      else await axios.post(`${API_URL}/nutrition-plans`, cuerpo, cfg);
      onGuardado();
    } catch (err) {
      setError(mensajeError(err, "No se pudo guardar el plan."));
      setGuardando(false);
    }
  };

  const dia = Object.fromEntries(
    MACROS.map(([k]) => [k, plan.comidas.reduce((s, c) => s + (c.opciones[0] ? total(c.opciones[0], k) : 0), 0)])
  );

  return (
    <form className="nutri-editor" onSubmit={guardar}>
      <button type="button" className="app-btn app-btn-quiet app-btn-sm admin-volver" onClick={onCancelar}>
        ← Volver a planes
      </button>

      <section className="app-card">
        <div className="form-group">
          <label htmlFor="plan-nombre">Nombre del plan</label>
          <input
            id="plan-nombre"
            className="app-input"
            value={plan.nombre}
            onChange={(e) => cambiar("nombre", e.target.value)}
            placeholder="Ej.: Definición 1.800 kcal"
            maxLength={80}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="plan-indicaciones">Indicaciones para el cliente</label>
          <textarea
            id="plan-indicaciones"
            className="app-input"
            rows={3}
            value={plan.indicaciones || ""}
            onChange={(e) => cambiar("indicaciones", e.target.value)}
            placeholder="Ej.: Bebe 2 litros de agua al día. Puedes cambiar la cena por la del almuerzo."
            maxLength={2000}
          />
        </div>
        <fieldset className="nutri-objetivos">
          <legend>Objetivo diario</legend>
          <p className="nutri-meta">Déjalo vacío para calcularlo con el peso, la altura y el objetivo de cada cliente.</p>
          <div className="nutri-campos">
            {MACROS.map(([k, label]) => (
              <label key={k} className="nutri-campo">
                <span>{label}</span>
                <input
                  type="number"
                  min="0"
                  className="app-input"
                  placeholder="Auto"
                  value={plan.objetivos?.[k] ?? ""}
                  onChange={(e) => cambiar("objetivos", { ...plan.objetivos, [k]: e.target.value })}
                />
              </label>
            ))}
          </div>
        </fieldset>
      </section>

      <datalist id="nutri-recetas">
        {recetas.map((r) => (
          <option key={r._id} value={r.nombre}>
            {r.tipo} · {r.calorias} kcal
          </option>
        ))}
      </datalist>
      <datalist id="nutri-tipos">
        {TIPOS.concat("Pre-entreno", "Post-entreno").map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>

      {plan.comidas.map((comida, i) => (
        <section key={i} className="app-card nutri-comida">
          <div className="nutri-comida-cabecera">
            <input
              className="app-input nutri-comida-tipo"
              list="nutri-tipos"
              value={comida.tipo}
              onChange={(e) => cambiarComida(i, { tipo: e.target.value })}
              aria-label="Nombre de la comida"
              maxLength={40}
              required
            />
            <button
              type="button"
              className="app-btn app-btn-quiet app-btn-sm"
              onClick={() => cambiar("comidas", plan.comidas.filter((_, j) => j !== i))}
              disabled={plan.comidas.length === 1}
            >
              Quitar comida
            </button>
          </div>

          {comida.opciones.map((o, k) => (
            <div key={k} className="nutri-opcion">
              <span className="nutri-opcion-num">Opción {k + 1}</span>
              {o.libre ? (
                <div className="nutri-campos">
                  <label className="nutri-campo nutri-campo-ancho">
                    <span>Alimento</span>
                    <input
                      className="app-input"
                      value={o.nombre}
                      onChange={(e) => cambiarOpcion(i, k, { nombre: e.target.value })}
                      placeholder="Ej.: 1 pieza de fruta"
                      maxLength={120}
                      required
                    />
                  </label>
                  {MACROS.map(([m, label]) => (
                    <label key={m} className="nutri-campo">
                      <span>{label}</span>
                      <input
                        type="number"
                        min="0"
                        className="app-input"
                        value={o[m] ?? ""}
                        onChange={(e) => cambiarOpcion(i, k, { [m]: e.target.value })}
                        required={m === "calorias"}
                      />
                    </label>
                  ))}
                </div>
              ) : (
                <div className="nutri-campos">
                  <label className="nutri-campo nutri-campo-ancho">
                    <span>Receta</span>
                    <input
                      className="app-input"
                      list="nutri-recetas"
                      value={o.nombre}
                      onChange={(e) => elegirReceta(i, k, e.target.value)}
                      placeholder="Escribe para buscar en la librería"
                      required
                    />
                  </label>
                  <label className="nutri-campo">
                    <span>Raciones</span>
                    <input
                      type="number"
                      min="0.25"
                      max="10"
                      step="0.25"
                      className="app-input"
                      value={o.raciones}
                      onChange={(e) => cambiarOpcion(i, k, { raciones: e.target.value })}
                      required
                    />
                  </label>
                </div>
              )}
              <div className="nutri-opcion-pie">
                <span className="nutri-meta">
                  {total(o, "calorias")} kcal · P {total(o, "proteinas")} g · C {total(o, "carbohidratos")} g · G{" "}
                  {total(o, "grasas")} g
                </span>
                <button
                  type="button"
                  className="nutri-quitar"
                  onClick={() => cambiarComida(i, { opciones: comida.opciones.filter((_, j) => j !== k) })}
                >
                  Quitar opción
                </button>
              </div>
            </div>
          ))}

          {comida.opciones.length < 5 && (
            <div className="nutri-acciones">
              <button
                type="button"
                className="app-btn app-btn-quiet app-btn-sm"
                onClick={() => cambiarComida(i, { opciones: [...comida.opciones, { nombre: "", raciones: 1 }] })}
              >
                + Receta
              </button>
              <button
                type="button"
                className="app-btn app-btn-quiet app-btn-sm"
                onClick={() =>
                  cambiarComida(i, { opciones: [...comida.opciones, { libre: true, nombre: "", raciones: 1 }] })
                }
              >
                + Alimento libre
              </button>
            </div>
          )}
        </section>
      ))}

      {plan.comidas.length < 10 && (
        <button
          type="button"
          className="app-btn app-btn-quiet nutri-anadir"
          onClick={() => cambiar("comidas", [...plan.comidas, { tipo: "Snack", opciones: [] }])}
        >
          + Añadir comida
        </button>
      )}

      <section className="app-panel nutri-resumen" aria-label="Total del día">
        <span>Total del día con la opción 1 de cada comida</span>
        <div className="nutri-resumen-cifras">
          {MACROS.map(([k, label]) => (
            <div key={k}>
              <strong>
                {dia[k]}
                {plan.objetivos?.[k] ? <small> / {plan.objetivos[k]}</small> : null}
              </strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="app-card">
        <h3 className="nutri-h3">Asignar a clientes</h3>
        {usuarios.length === 0 && <p className="nutri-meta">No hay clientes.</p>}
        <ul className="nutri-clientes">
          {usuarios.map((u) => (
            <li key={u._id}>
              <label>
                <input
                  type="checkbox"
                  checked={plan.asignados.includes(u._id)}
                  onChange={(e) =>
                    cambiar(
                      "asignados",
                      e.target.checked ? [...plan.asignados, u._id] : plan.asignados.filter((id) => id !== u._id)
                    )
                  }
                />
                <span>
                  {`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email}
                  <small> · {u.email}</small>
                  {u.planNutricional && u.planNutricional !== plan._id && !plan.asignados.includes(u._id) && (
                    <small> · ya tiene otro plan</small>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
        <p className="nutri-meta">Cada cliente tiene un solo plan: al asignarle este, deja el que tuviera.</p>
      </section>

      {error && <div className="form-error">{error}</div>}
      <div className="nutri-acciones">
        <button type="submit" className="app-btn" disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar plan"}
        </button>
        <button type="button" className="app-btn app-btn-quiet" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

/* ---------- Recetas ---------- */

const recetaVacia = {
  nombre: "",
  tipo: "Almuerzo",
  calorias: "",
  proteinas: "",
  carbohidratos: "",
  grasas: "",
  tiempo: "",
  ingredientes: "",
  descripcion: "",
};

function Recetas({ recetas, recargar }) {
  const [buscar, setBuscar] = useState("");
  const [tipo, setTipo] = useState("");
  const [editando, setEditando] = useState(null);
  const [borrar, setBorrar] = useState(false);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const texto = buscar.trim().toLowerCase();
  const filtradas = recetas.filter(
    (r) =>
      (!tipo || r.tipo === tipo) &&
      (!texto || r.nombre.toLowerCase().includes(texto) || r.ingredientes?.some((i) => i.toLowerCase().includes(texto)))
  );

  const abrir = (r) => {
    setError("");
    setBorrar(false);
    setEditando(
      r
        ? {
            ...recetaVacia,
            ...r,
            // Los estimados se enseñan como pista, no como dato escrito
            ...(r.estimados ? { proteinas: "", carbohidratos: "", grasas: "" } : {}),
            tiempo: r.tiempo ?? "",
            ingredientes: (r.ingredientes || []).join(", "),
          }
        : { ...recetaVacia }
    );
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    const cuerpo = {
      ...editando,
      ingredientes: editando.ingredientes
        .split(",")
        .map((i) => i.trim())
        .filter(Boolean),
    };
    try {
      const cfg = await conToken();
      if (editando._id) await axios.put(`${API_URL}/recipes/${editando._id}`, cuerpo, cfg);
      else await axios.post(`${API_URL}/recipes`, cuerpo, cfg);
      await recargar();
      setEditando(null);
    } catch (err) {
      setError(mensajeError(err, "No se pudo guardar la receta."));
    }
    setGuardando(false);
  };

  const borrarReceta = async () => {
    try {
      await axios.delete(`${API_URL}/recipes/${editando._id}`, await conToken());
      await recargar();
      setEditando(null);
    } catch (err) {
      setError(mensajeError(err, "No se pudo borrar la receta."));
    }
  };

  if (editando) {
    const original = recetas.find((r) => r._id === editando._id);
    const campo = (k) => ({
      value: editando[k],
      onChange: (e) => setEditando((r) => ({ ...r, [k]: e.target.value })),
    });
    return (
      <form className="nutri-editor" onSubmit={guardar}>
        <button type="button" className="app-btn app-btn-quiet app-btn-sm admin-volver" onClick={() => setEditando(null)}>
          ← Volver a recetas
        </button>
        <section className="app-card">
          <div className="nutri-campos">
            <label className="nutri-campo nutri-campo-ancho">
              <span>Nombre</span>
              <input className="app-input" maxLength={120} required {...campo("nombre")} />
            </label>
            <label className="nutri-campo">
              <span>Tipo</span>
              <select className="app-input" {...campo("tipo")}>
                {TIPOS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="nutri-campo">
              <span>Tiempo (min)</span>
              <input type="number" min="0" className="app-input" {...campo("tiempo")} />
            </label>
          </div>
          <p className="nutri-meta">Valores de una ración. Si dejas los macros vacíos, se estiman con los ingredientes.</p>
          <div className="nutri-campos">
            {MACROS.map(([k, label]) => (
              <label key={k} className="nutri-campo">
                <span>{label}</span>
                <input
                  type="number"
                  min="0"
                  className="app-input"
                  required={k === "calorias"}
                  placeholder={original?.estimados && k !== "calorias" ? `≈ ${original[k]}` : "Auto"}
                  {...campo(k)}
                />
              </label>
            ))}
          </div>
          <div className="form-group">
            <label htmlFor="receta-ingredientes">Ingredientes, separados por comas</label>
            <input
              id="receta-ingredientes"
              className="app-input"
              placeholder="pechuga de pollo, arroz integral, brócoli"
              {...campo("ingredientes")}
            />
          </div>
          <div className="form-group">
            <label htmlFor="receta-descripcion">Preparación</label>
            <textarea id="receta-descripcion" className="app-input" rows={3} maxLength={2000} {...campo("descripcion")} />
          </div>
          {editando.fuente && (
            <p className="nutri-meta">Adaptada de Wikilibros (CC BY-SA): al editarla, sigue enlazando a su fuente.</p>
          )}
        </section>
        {error && <div className="form-error">{error}</div>}
        <div className="nutri-acciones">
          <button type="submit" className="app-btn" disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar receta"}
          </button>
          {editando._id &&
            (borrar ? (
              <button type="button" className="app-btn app-btn-danger" onClick={borrarReceta}>
                Confirmar borrado
              </button>
            ) : (
              <button type="button" className="app-btn app-btn-quiet" onClick={() => setBorrar(true)}>
                Borrar receta
              </button>
            ))}
        </div>
        {editando._id && (
          <p className="nutri-meta">Los planes ya creados guardan su propia copia: no cambian al editar o borrar la receta.</p>
        )}
      </form>
    );
  }

  return (
    <>
      <div className="nutri-barra">
        <input
          className="app-input nutri-buscar"
          type="search"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          placeholder="Buscar por nombre o ingrediente"
          aria-label="Buscar receta"
        />
        <select className="app-input nutri-filtro" value={tipo} onChange={(e) => setTipo(e.target.value)} aria-label="Tipo">
          <option value="">Todos los tipos</option>
          {TIPOS.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <button className="app-btn" onClick={() => abrir(null)}>
          + Nueva receta
        </button>
      </div>
      <p className="nutri-meta">
        {filtradas.length > MAX_LISTA
          ? `Mostrando ${MAX_LISTA} de ${filtradas.length} recetas: afina la búsqueda.`
          : `${filtradas.length} recetas.`}{" "}
        ≈ = macros estimados.
      </p>
      <ul className="nutri-recetas">
        {filtradas.slice(0, MAX_LISTA).map((r) => (
          <li key={r._id}>
            <button type="button" className="nutri-receta" onClick={() => abrir(r)}>
              <span className="nutri-receta-nombre">{r.nombre}</span>
              <span className="nutri-meta">
                {r.tipo} · {r.calorias} kcal · {r.estimados ? "≈ " : ""}P {r.proteinas} · C {r.carbohidratos} · G {r.grasas}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
